import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Formik, Form } from 'formik'
import isEmail from 'validator/lib/isEmail'
import axios from 'axios'
import { BackLink, FormField, PageHero, ResultDialog, SubmitButton } from '../../components/ui/ui'
import './JobApplication.scss'

const FORM_ENDPOINT = 'https://formspree.io/f/mrgdzgnr'

const buildMessage = (values, jobTitle) => {
  if (!values.full_name.trim()) return ''
  const experience = values.years_experience ? `with ${values.years_experience} years of experience` : 'with relevant experience'
  const portfolio = values.portfolio_link ? ` My portfolio is at ${values.portfolio_link}.` : ''
  const cv = values.cv_link ? ` You can find my CV at ${values.cv_link}.` : ''
  return `Hi, I'm ${values.full_name.trim()}. I'm interested in the ${jobTitle} vacancy ${experience}.${portfolio}${cv} I'm excited about the opportunity to contribute to your team and would love to discuss how my skills align with your requirements. Thank you for considering my application.`
}

const steps = ['About you', 'Links', 'Message']

const JobApplication = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [status, setStatus] = useState('idle')
  const [dialog, setDialog] = useState(null)
  const [userEditedMessage, setUserEditedMessage] = useState(false)

  const job = location.state?.job || { title: 'Open application', department: 'Engineering' }
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/'))

  const initialValues = { full_name: '', email: '', years_experience: '', portfolio_link: '', cv_link: '', message: '' }

  const validate = (values) => {
    const errors = {}
    if (!values.full_name.trim()) errors.full_name = 'Your name is required'
    if (!values.email) errors.email = 'Your email is required'
    else if (!isEmail(values.email)) errors.email = "That email doesn't look right"
    if (values.years_experience && Number(values.years_experience) < 0) errors.years_experience = 'Must be 0 or more'
    if (!values.message.trim()) errors.message = 'Add a short message'
    return errors
  }

  const onSubmit = async (values, { resetForm }) => {
    setStatus('loading')
    try {
      const response = await axios.post(FORM_ENDPOINT, {
        name: values.full_name,
        email: values.email,
        message: values.message || buildMessage(values, job.title),
        subject: `Job Application: ${job.title}`,
        job_title: job.title,
        department: job.department,
        years_experience: values.years_experience,
        portfolio: values.portfolio_link,
        cv: values.cv_link
      })
      if (response.status !== 200) throw new Error('Unexpected response')
      setStatus('success')
      resetForm()
      setUserEditedMessage(false)
      setDialog({ type: 'success', title: 'Application sent!', message: 'Thanks for applying. The team will review it and get in touch shortly.' })
    } catch (error) {
      setStatus('error')
      setDialog({ type: 'error', title: 'Application not sent', message: 'Something went wrong. Please try again in a moment.' })
    }
  }

  // Filled fields per step, used for the progress bar.
  const progressOf = (values) => [
    Boolean(values.full_name && values.email),
    Boolean(values.portfolio_link || values.cv_link || values.years_experience),
    Boolean(values.message.trim())
  ]

  return (
    <main className="ui-page form-page">
      <PageHero
        back={<BackLink onClick={goBack}>Back</BackLink>}
        eyebrow="Job application"
        title={<>Apply for <span className="ui-gradient-text">{job.title}</span></>}
        subtitle="Fill in a few details and a tailored application message writes itself. Edit it however you like before sending."
      />

      <section className="ui-container form-page__body">
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
          {(formik) => {
            const progress = progressOf(formik.values)
            const done = progress.filter(Boolean).length

            // Update a field and, until the visitor edits the message, regenerate the draft.
            const handleAutoField = (event) => {
              const { name, value } = event.target
              formik.setFieldValue(name, value)
              if (!userEditedMessage) {
                const draft = buildMessage({ ...formik.values, [name]: value }, job.title)
                formik.setFieldValue('message', draft)
              }
            }

            return (
              <div className="form-page__layout">
                <aside className="ui-card form-summary">
                  <div className="form-summary__row"><i className="fas fa-briefcase" /><div><small>Position</small><strong>{job.title}</strong></div></div>
                  <div className="form-summary__row"><i className="fas fa-building" /><div><small>Department</small><strong>{job.department}</strong></div></div>
                  <div className="form-progress">
                    <div className="form-progress__head"><span>Progress</span><strong>{Math.round((done / steps.length) * 100)}%</strong></div>
                    <div className="form-progress__bar"><span style={{ transform: `scaleX(${done / steps.length})` }} /></div>
                    <ol>
                      {steps.map((step, index) => (
                        <li key={step} className={progress[index] ? 'is-done' : ''}>
                          <i className={`fas ${progress[index] ? 'fa-circle-check' : 'fa-circle'}`} /> {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                </aside>

                <div className="ui-card form-main">
                  <Form noValidate>
                    <div className="ui-form-grid">
                      <FormField name="full_name" label="Full name" autoComplete="name" required onChange={handleAutoField} />
                      <FormField name="email" label="Email address" type="email" autoComplete="email" required />
                      <FormField name="years_experience" label="Years of experience" type="number" min="0" onChange={handleAutoField} />
                      <FormField name="portfolio_link" label="Portfolio link" type="url" hint="Optional" onChange={handleAutoField} />
                      <FormField className="span-2" name="cv_link" label="CV / résumé link" type="url" hint="Google Drive, Dropbox or any public link" onChange={handleAutoField} />
                      <FormField
                        className="span-2"
                        name="message"
                        label="Application message"
                        as="textarea"
                        rows="8"
                        maxLength={3000}
                        required
                        hint={userEditedMessage ? 'Edited by you' : 'Auto-generated from your details. Click to edit.'}
                        onChange={(event) => { formik.handleChange(event); setUserEditedMessage(true) }}
                      />
                    </div>
                    {userEditedMessage && (
                      <button
                        type="button"
                        className="form-regen"
                        onClick={() => { formik.setFieldValue('message', buildMessage(formik.values, job.title)); setUserEditedMessage(false) }}
                      >
                        <i className="fas fa-wand-magic-sparkles" /> Regenerate message
                      </button>
                    )}
                    <div className="form-actions">
                      <SubmitButton status={status} labels={{ idle: 'Submit application', loading: 'Submitting…', success: 'Submitted!' }} />
                    </div>
                  </Form>
                </div>
              </div>
            )
          }}
        </Formik>
      </section>

      <ResultDialog
        open={Boolean(dialog)}
        {...(dialog || {})}
        onClose={() => {
          const succeeded = dialog?.type === 'success'
          setDialog(null)
          setStatus('idle')
          if (succeeded) navigate('/')
        }}
      />
    </main>
  )
}

export default JobApplication
